import { KINDS } from '../nucleus/RealmsModel';
import { STATUS_WORDS } from '../../components/nucleus/status';

// ACCESS — one grant per connection: who sees, through which realms, as which
// identity, until when. Grants are made on the consent page (/grant); here they
// are read, paused, re-attached, renewed and disconnected.

// The kinds of connection, as the address spells them (?kind=apps) and as read.
// An agent (a "delegate") is something an app runs on your behalf, holding its
// own grant, narrower than its app's.
export const PAGE_KIND = { apps: 'app', people: 'person', groups: 'group', agents: 'delegate', members: 'member', 'cosm-agents': 'cosm_agent', tokens: 'token' };
export const KIND_WORD = { app: 'Apps', person: 'People', group: 'Groups', delegate: 'Agents', cosm_agent: 'COSM agents', automation: 'Automations', plugin: 'Plugins', public: 'The public', member: 'Members', token: 'Tokens' };

// The pages Realms & Contracts used to have, as the filters that replaced them
// (Exposure profiles, filtered). An old address lands on its filter.
export const OLD_PAGES = {
    grants: {}, health: {}, ended: { status: 'inactive' },
    apps: { kind: 'apps' }, people: { kind: 'people' }, groups: { kind: 'groups' }, agents: { kind: 'agents' },
};

export const KIND_ORDER = KINDS.map((k) => k.id);

const DAY = 86_400_000;
export const lapsingSoon = (p, now = Date.now()) => !!(p.live && p.expires && p.expires.getTime() < now + 14 * DAY);

// One entry per connection (an app, a person, a group, an agent), every grant
// it ever held: the live ones first, newest first. `first` is the one shown: the
// one in use, which for an app is the newest live grant that is not paused (a
// request from the app resolves to it, ExposureProfileGrant.get_active_app_ep),
// else the newest live one, else the newest that ended.
export function connectionsOf(profiles) {
    const by = new Map();
    for (const p of profiles) {
        if (!by.has(p.key)) by.set(p.key, []);
        by.get(p.key).push(p);
    }
    const newest = (a, b) => (b.created?.getTime() || 0) - (a.created?.getTime() || 0);
    return [...by.entries()].map(([key, all]) => {
        const live = all.filter((p) => p.live).sort(newest);
        const ended = all.filter((p) => !p.live).sort(newest);
        const first = live.find((p) => p.active) || live[0] || ended[0];
        return { key, kind: all[0].kind, all: [...live, ...ended], live, ended, first };
    });
}

/** An app's live contract that a newer one has replaced: requests from the app
 *  use the newest live one that is not paused, so this one is not used. Returns
 *  the contract in use, or null. */
export function replacedBy(p, connection) {
    const used = connection?.first;
    if (!used || used.id === p.id || p.entityType !== 'app' || p.subKey) return null;
    if (!p.live || !p.active || !used.live || !used.active) return null;
    return used;
}

// ?open= names a connection as kind:entityId:subKey. Links written elsewhere
// say `app:<id>:` (an empty sub) or `app:<id>:<sub>` for an agent, whose kind
// here is `delegate`; a grant's own id opens it too. The id and the sub decide;
// the kind only breaks a tie.
export function findConnection(connections, open) {
    if (!open) return null;
    const exact = connections.find((c) => c.key === open);
    if (exact) return exact;
    const [kind, id, ...rest] = String(open).split(':');
    const sub = rest.join(':');
    if (id) {
        const hits = connections.filter((c) => String(c.first.entityId) === id && (c.first.subKey || '') === sub);
        if (hits.length) return hits.find((c) => c.kind === kind || (kind === 'app' && c.kind === 'delegate')) || hits[0];
    }
    return connections.find((c) => c.all.some((p) => String(p.id) === String(open))) || null;
}

export function statusOf(p) {
    if (p.revoked) return 'revoked';
    if (p.expired) return 'expired';
    if (p.waiting) return 'waiting';
    // a schedule's or an automation's contract is stored paused by design and never
    // paused by you: whether it runs is its automation's (standing.js)
    if (p.entityType === 'automation') return 'active';
    return p.active ? 'active' : 'paused';
}
// the consent page's words for a grant's state (useGrant.js: paused, expired, revoked),
// and a friend request's offer, waiting on their answer
export const STATUS_WORD = STATUS_WORDS.contract;
// one grant's state in a sentence's words: whom a waiting offer waits on
export const statusText = (p) => (statusOf(p) === 'waiting' ? `Waiting for ${p.label} to accept` : STATUS_WORD[statusOf(p)]);

// The consent page, asked to edit (or renew, or make again) one grant; it comes
// back here with the connection open. Null when the page cannot name who it is.
export function consentHref(p, { groupId, back, edit = true }) {
    // a token is made and changed nowhere but its own sheet: it keeps what it was made with
    if (p.kind === 'token') return null;
    const q = new URLSearchParams();
    const t = p.entityType;
    if (t === 'app') {
        if (!p.clientId) return null;
        q.set('client_id', p.clientId);
        if (p.subKey) q.set('sub_entity_key', p.subKey);
        if (edit) q.set('edit_auth_id', p.id);
    } else {
        if (t === 'friend' || t === 'user' || t === 'group_member') q.set('user_id', p.entityId);
        else if (t === 'group' || t === 'group_connection') q.set('to_group_id', p.entityId);
        else if (t === 'public') q.set('public', 'true');
        else return null;
        if (edit) q.set('edit_ep_id', p.id);
    }
    if (groupId) q.set('group_id', groupId);
    q.set('redirect_uri', back);
    return `/grant?${q.toString()}`;
}

// Where the connection itself lives in Nucleus: its page, opened on it.
export function homeOf(p, to) {
    if (p.kind === 'token') return null;     // its home is here, among the contracts
    if (p.kind === 'delegate') return { label: 'Open in Agents', href: to('apps', 'agents', `open=${p.id}`) };
    if (p.kind === 'app') return { label: 'Open in Apps', href: to('apps', null, `open=${p.entityId}`) };
    if (p.kind === 'person') return { label: 'Open in People', href: to('people', null, `open=${p.entityId}`) };
    if (p.kind === 'member') return { label: 'Open member', href: to('people', null, `open=${p.entityId}`) };
    if (p.entityType === 'group' || p.entityType === 'group_connection') return { label: 'Open in Groups', href: `/groups?open=${p.entityId}` };
    return null;
}
